import type { BrandSurface } from '@/types/portal';

/**
 * Browser persistence for the branding settings the database has no columns
 * for (see docs/BACKEND_GAPS.md): the branded-surface toggles and the wordmark
 * and portal-avatar logos. The profile, colours and logo mark come from the API.
 *
 * Surface toggles live in localStorage. Logos are image data URLs that can
 * reach a couple of megabytes each, so they live in IndexedDB instead.
 */

export interface BrandingSnapshot {
  surfaces: BrandSurface[];
}

const STORAGE_KEY = 'twiva.branding.v2';
const LOGO_DB_NAME = 'twiva-branding';
const LOGO_STORE = 'logos';
const LOGO_RECORD_KEY = 'all';

// ---------------------------------------------------------------------------
// Surfaces (localStorage)
// ---------------------------------------------------------------------------

export function loadBrandingSnapshot(): Partial<BrandingSnapshot> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Partial<BrandingSnapshot>;
  } catch {
    return null;
  }
}

export function saveBrandingSnapshot(snapshot: BrandingSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* Quota errors or privacy mode — branding simply won't persist. */
  }
}

// ---------------------------------------------------------------------------
// Logo assets (IndexedDB)
// ---------------------------------------------------------------------------

function openLogoDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(LOGO_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LOGO_STORE)) {
        db.createObjectStore(LOGO_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function loadLogos(): Promise<Record<string, string>> {
  try {
    const db = await openLogoDb();
    return await new Promise<Record<string, string>>((resolve, reject) => {
      const tx = db.transaction(LOGO_STORE, 'readonly');
      const request = tx.objectStore(LOGO_STORE).get(LOGO_RECORD_KEY);
      request.onsuccess = () => resolve((request.result as Record<string, string>) ?? {});
      request.onerror = () => reject(request.error);
    });
  } catch {
    return {};
  }
}

export async function saveLogos(logos: Record<string, string>): Promise<void> {
  try {
    const db = await openLogoDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(LOGO_STORE, 'readwrite');
      tx.objectStore(LOGO_STORE).put(logos, LOGO_RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* ignore — logos simply won't persist */
  }
}